import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Container } from "@/components/PageShell";
import { StatusDot } from "@/components/Badges";
import { Server, Users, GitBranch, Activity } from "lucide-react";
import { Link } from "react-router-dom";

function StatCard({ icon: Icon, label, value, hint }) {
    return (
        <div className="bg-white border border-zinc-200 rounded-md p-5">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{label}</div>
                    <div className="text-3xl font-semibold tracking-tight mt-2 font-mono">{value}</div>
                    {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
                </div>
                <div className="w-9 h-9 rounded-md bg-zinc-100 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-zinc-600" strokeWidth={1.75} />
                </div>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/stats").then((r) => setStats(r.data)).catch((e) => setErr(e.message));
    }, []);

    return (
        <>
            <PageHeader title="Обзор" subtitle="Состояние VPN-инфраструктуры" testid="dashboard-header" />
            <Container>
                {err && <div className="text-red-600 text-sm">{err}</div>}
                {!stats ? (
                    <div className="text-sm text-zinc-500">Загрузка…</div>
                ) : (
                    <div data-testid="dashboard-content">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <StatCard
                                icon={Server} label="Серверы"
                                value={stats.servers_total}
                                hint={`${stats.servers_by_status.online || 0} online · ${stats.servers_by_status.offline || 0} offline`}
                            />
                            <StatCard
                                icon={Users} label="Пользователи VPN"
                                value={stats.users_total}
                                hint={`${stats.users_enabled} активных`}
                            />
                            <StatCard
                                icon={GitBranch} label="Маршруты"
                                value={stats.chains_total}
                                hint={`${stats.chains_active} активных`}
                            />
                            <StatCard
                                icon={Activity} label="Деплои за 24ч"
                                value={stats.recent_deployments.length}
                                hint="см. журнал"
                            />
                        </div>

                        {/* Server status breakdown */}
                        <div className="mt-8 bg-white border border-zinc-200 rounded-md">
                            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-semibold">Состояние серверов</div>
                                    <div className="text-xs text-zinc-500">Распределение по статусам</div>
                                </div>
                                <Link to="/servers" className="text-xs text-[#FA3E05] font-medium hover:underline">
                                    Перейти к серверам →
                                </Link>
                            </div>
                            <div className="p-5 grid grid-cols-2 md:grid-cols-6 gap-4">
                                {["online", "deployed", "deploying", "offline", "error", "unknown"].map((s) => (
                                    <div key={s} className="flex items-center gap-2">
                                        <StatusDot status={s} />
                                        <div className="text-xs">
                                            <div className="font-mono text-zinc-600 uppercase tracking-wider text-[10px]">{s}</div>
                                            <div className="font-mono text-base tabular-nums">{stats.servers_by_status[s] || 0}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recent deployments */}
                        <div className="mt-6 bg-white border border-zinc-200 rounded-md">
                            <div className="px-5 py-4 border-b border-zinc-200">
                                <div className="text-sm font-semibold">Последние деплои</div>
                            </div>
                            {stats.recent_deployments.length === 0 ? (
                                <div className="p-8 text-center text-sm text-zinc-500">Деплоев ещё не было</div>
                            ) : (
                                <div className="divide-y divide-zinc-100">
                                    {stats.recent_deployments.map((d) => (
                                        <Link
                                            to={`/deployments?id=${d.id}`}
                                            key={d.id}
                                            data-testid={`recent-deployment-${d.id}`}
                                            className="flex items-center gap-4 px-5 py-3 hover:bg-zinc-50 transition-colors"
                                        >
                                            <StatusDot status={d.status} />
                                            <div className="text-sm font-mono flex-1 truncate">{d.action}</div>
                                            <div className="text-xs text-zinc-500 font-mono">
                                                {new Date(d.started_at).toLocaleString("ru-RU")}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Container>
        </>
    );
}
